import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
    Crown,
    ArrowRight,
    Lightning,
    Infinity as InfinityIcon,
    Timer,
    X,
} from "@phosphor-icons/react";
import api from "@/lib/api";

const PERKS = [
    "Every Recipe + Playbook in the Vault — yours forever",
    "Every future blueprint we drop, free for life",
    "$447 once · vs $240/yr forever",
    "LIFETIME FOUNDER badge on the leaderboard",
];

function formatCountdown(ms) {
    if (ms <= 0) return "00:00:00";
    const totalSeconds = Math.floor(ms / 1000);
    const h = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
    const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
    const s = String(totalSeconds % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
}

/**
 * Post-Order-Bump Lifetime upsell modal.
 *
 * Shown immediately after a successful Order Bump purchase (or any payment
 * that opens the 24h post-bump window) when the user is NOT already a
 * lifetime member and has NOT used the discount yet.
 *
 * Props:
 *  - eligibleUntil: ISO string from `user.lifetime_upsell_eligible_until`
 *  - onDismiss:    callback when user closes / declines (we never re-show in same session)
 */
export default function PostBumpUpsellModal({ eligibleUntil, onDismiss }) {
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(false);
    const [remainingMs, setRemainingMs] = useState(() => {
        if (!eligibleUntil) return 0;
        return new Date(eligibleUntil).getTime() - Date.now();
    });

    // Tick the countdown every second
    useEffect(() => {
        if (!eligibleUntil) return;
        const tick = () => {
            const ms = new Date(eligibleUntil).getTime() - Date.now();
            setRemainingMs(ms);
            if (ms <= 0) {
                setOpen(false);
                onDismiss?.();
            }
        };
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [eligibleUntil, onDismiss]);

    const close = useCallback(() => {
        setOpen(false);
        onDismiss?.();
    }, [onDismiss]);

    const claim = async () => {
        setLoading(true);
        try {
            const r = await api.post("/stripe/create-lifetime-checkout", {
                origin_url: window.location.origin,
                promo: "POST_BUMP_50_OFF",
            });
            if (r.data?.url) {
                window.location.href = r.data.url;
                return;
            }
            toast.error("Could not start upgrade checkout");
            setLoading(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upgrade failed");
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="post-bump-upsell-modal"
            className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-black/90 backdrop-blur-sm animate-fade-in"
            role="dialog"
            aria-modal="true"
            aria-labelledby="post-bump-upsell-title"
        >
            <div className="relative w-full max-w-2xl bg-[#0A0A0A] border border-electric-blue neon-border overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric-blue to-transparent" />
                <div className="absolute -top-24 -right-24 w-72 h-72 bg-electric-blue/15 rounded-full blur-3xl pointer-events-none" />

                {/* Close */}
                <button
                    onClick={close}
                    data-testid="post-bump-upsell-close-btn"
                    aria-label="Close offer"
                    className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-white transition-colors z-10"
                >
                    <X size={18} weight="bold" />
                </button>

                <div className="relative p-8 md:p-10">
                    {/* Eyebrow */}
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-electric-blue/40 bg-electric-blue/10 mb-5">
                        <Crown size={14} weight="fill" className="text-electric-blue" />
                        <span className="label-mono text-[10px] text-electric-blue">
                            FOUNDER-ONLY · ONE-TIME OFFER
                        </span>
                    </div>

                    {/* Headline */}
                    <h2
                        id="post-bump-upsell-title"
                        className="font-heading text-3xl md:text-5xl font-black tracking-tighter leading-[1.05] mb-4"
                    >
                        Since you're already in,{" "}
                        <span className="text-electric-blue neon-text">save $50</span>{" "}
                        and own the entire Vault forever.
                    </h2>

                    <p className="text-sm md:text-base text-foreground/80 leading-relaxed mb-6 max-w-xl">
                        You just unlocked the Visual Authority Guide. Right now — and only
                        right now — upgrade to{" "}
                        <span className="text-white font-bold">Lifetime Vault Access</span>{" "}
                        for a one-time payment of{" "}
                        <span className="text-electric-blue font-bold">$447</span>{" "}
                        instead of $497. Never billed again. Ever.
                    </p>

                    {/* Perk list */}
                    <ul className="space-y-2 mb-6">
                        {PERKS.map((perk, idx) => (
                            <li
                                key={perk}
                                data-testid={`post-bump-perk-${idx}`}
                                className="flex items-start gap-2.5 text-sm text-foreground/90"
                            >
                                <Lightning
                                    size={14}
                                    weight="fill"
                                    className="text-electric-blue mt-0.5 flex-shrink-0"
                                />
                                <span>{perk}</span>
                            </li>
                        ))}
                    </ul>

                    {/* Price + Countdown */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 sm:gap-6 mb-6">
                        <div>
                            <div className="flex items-baseline gap-3">
                                <span className="font-heading text-6xl font-black text-electric-blue">
                                    $447
                                </span>
                                <span className="text-2xl line-through text-muted-foreground/70 font-bold">
                                    $497
                                </span>
                            </div>
                            <p
                                data-testid="post-bump-discount-label"
                                className="label-mono text-[10px] text-electric-blue mt-1"
                            >
                                $50 OFF · ONE-TIME · LIFETIME ACCESS
                            </p>
                        </div>

                        {remainingMs > 0 && (
                            <div
                                data-testid="post-bump-countdown"
                                className="flex items-center gap-2 px-3 py-2 border border-electric-blue/30 bg-black/60"
                            >
                                <Timer
                                    size={14}
                                    weight="bold"
                                    className="text-electric-blue animate-pulse"
                                />
                                <div>
                                    <p className="label-mono text-[9px] text-muted-foreground leading-none mb-0.5">
                                        OFFER EXPIRES IN
                                    </p>
                                    <p className="font-mono text-sm font-bold text-electric-blue tracking-wider">
                                        {formatCountdown(remainingMs)}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* CTA stack */}
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={claim}
                            disabled={loading}
                            data-testid="post-bump-claim-btn"
                            className="w-full bg-electric-blue hover:bg-electric-blue-dark disabled:opacity-50 text-white font-bold py-4 text-sm tracking-wider uppercase transition-all hover:shadow-[0_0_32px_rgba(0,123,255,0.7)] flex items-center justify-center gap-2 animate-glow-pulse"
                        >
                            {loading ? (
                                <>
                                    <Lightning size={16} weight="fill" className="animate-pulse" />
                                    REDIRECTING TO STRIPE...
                                </>
                            ) : (
                                <>
                                    <InfinityIcon size={16} weight="bold" />
                                    YES · CLAIM MY $50 DISCOUNT
                                    <ArrowRight size={16} weight="bold" />
                                </>
                            )}
                        </button>

                        <button
                            onClick={close}
                            data-testid="post-bump-decline-btn"
                            className="w-full text-xs text-muted-foreground hover:text-white tracking-wider uppercase py-2 transition-colors"
                        >
                            No thanks — continue to the Lab
                        </button>
                    </div>

                    <p className="text-center text-[10px] text-muted-foreground mt-4 italic">
                        This offer is unique to your seat and disappears when the timer runs out.
                    </p>
                </div>
            </div>
        </div>
    );
}
